/**
 * `ConversationShell` — the single chat-pane layout primitive.
 *
 * Generalizes the case chat / global home shell (a fixed-bottom composer + a
 * scrolling history body) so every chat surface renders the same way.
 *
 * Pure layout: NO chat logic, NO session knowledge, NO `useChatSession`.
 * Props are slots + the `ChatMessageList` inputs. The DOM identity
 * (`data-testid`/`role`/`aria-label`/`data-session-key`) is pass-through so
 * callers keep the exact attributes their e2e suites assert (e.g.
 * `case-workspace-chat-pane`, `role="main"`, `data-session-key`).
 *
 * Body composition: the optional `loading` + `fallback` slots live INSIDE
 * the scrolling body — the composer stays fixed-bottom.
 *   - `fallback` set (runtime-down surface) ⇒ render it instead of the list.
 *   - else render the optional `loading` node above the `ChatMessageList`.
 * Callers that pass neither slot get behavior-identical to a plain
 * `<ChatMessageList/>` inside `flex-1 overflow-auto`.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ChatErrorMessage } from "@lexiun/lx-llm-errors";
import { ChatMessageList } from "@/components/workspace/ChatMessageList";
import type { ChatMessage } from "@/types/gateway";
import type { ContentBlock } from "@/types/content-blocks";
import type { TurnActivity } from "@/stores/useChatStore";
import { cn } from "@/lib/utils";
import { useStickToBottom } from "@/hooks/useStickToBottom";
import { zoomForWidth } from "@/lib/conversation-zoom";
import { historyBodyState } from "@/lib/chat/history-body-state";

export interface ConversationShellProps {
  // ---- ChatMessageList inputs (the scrolling body) ----
  messages: ChatMessage[];
  /** Mid-flight assistant content as typed blocks (post chat-content-
   *  model redesign; replaces the legacy `streamingText: string`). */
  streamingBlocks: ContentBlock[];
  isStreaming: boolean;
  /** Live work state of the in-flight turn — forwarded to `ChatMessageList`,
   *  which renders the in-flow status line + work header at the tail. */
  activity?: TurnActivity;
  /** Stall affordance (operator directive 2026-08-14): rendered INSIDE the
   *  running turn's activity area, never as a composer banner. */
  stalled?: boolean;
  onReleaseStalledRun?: () => void;
  error: string | ChatErrorMessage | null;
  /** Forwarded to `ChatMessageList` — re-runs the most recent turn. */
  onRetry?: () => void;

  // ---- Layout slots ----
  /** Optional top bar. Case chat passes its "AI Chat" header; the global
   *  home omits it (Q-D — the greeting is folded into the opening message). */
  header?: ReactNode;
  /** Slot between the body and the composer (ContextIndicator / the global
   *  home's secondary nav-chip row — Q-A). Rendered OUTSIDE the zoom wrapper
   *  with the composer it belongs to (see the render site). */
  aboveInput?: ReactNode;
  /** The fixed-bottom composer. Callers keep it inside `ChatLLMKeysGate`
   *  exactly as before — the shell only positions it.
   *
   *  ⚠ **Rendered OUTSIDE the zoom wrapper (operator ruling 2026-08-10):** the
   *  promptbox keeps its own type at every pane width. Do not move it back in
   *  to make the miniature "faithful" — see the render site. */
  composer: ReactNode;
  /** Optional pinned strip BELOW the composer, inside the pane and
   *  width-coupled to it. Was the feedback chip (removed in B4c —
   *  per-response feedback now lives under each AI message); the chat
   *  infobar is the incoming tenant.
   *
   *  ⚠ **Rendered OUTSIDE the zoom wrapper.** This line used to say the
   *  opposite — it was left behind when the footer was moved out after its
   *  10px type measured 6.5px at viewer-open width. A stale claim here is
   *  load-bearing, because it is what a reader consults when choosing a font
   *  size for this slot. */
  footer?: ReactNode;
  /** Phase 6 — localized loading state, rendered IN the body. */
  loading?: ReactNode;
  /** Phase 8 — static fallback, rendered IN the body INSTEAD of the list. */
  fallback?: ReactNode;

  // ---- P6 item 2 — paged history (lexide-agent-bus-p6-spa-miniplan §3) ----
  /** THIRD STATE of the initial history read: the read FAILED. When set and
   *  the thread is empty, the body renders "could not load" INSTEAD of an
   *  empty transcript — empty WITHOUT it means no history; conflating the
   *  two shows a lawyer an empty case that is not empty. */
  historyReadError?: string | null;
  /** Older messages exist before the loaded window (measured server-side).
   *  Arms the top back-scroll sentinel. */
  hasMoreHistory?: boolean;
  /** A back-scroll page fetch is in flight (renders the top spinner row). */
  loadingOlder?: boolean;
  /** A back-scroll fetch failed (e.g. rejected cursor). Renders an inline
   *  row with an EXPLICIT retry — never auto-retried bare. */
  olderError?: string | null;
  /** Fetch the block before the oldest loaded message. Fired by the top
   *  sentinel entering view, and by the explicit retry affordance. */
  onLoadOlder?: () => void;

  // ---- Pass-through DOM identity (regression-critical for AIChatPane) ----
  testId?: string;
  role?: string;
  ariaLabel?: string;
  dataSessionKey?: string;
  /** Extra root classes; merged after the shell defaults. */
  className?: string;
  /**
   * Content zoom for compact mode. `"auto"` (default) derives a CSS `zoom`
   * factor from the shell's OWN measured width so the conversation shrinks into
   * a faithful miniature when a focus-pane viewer squeezes it into a left
   * column — and returns to full size when the window widens again. `1` (or any
   * explicit number) forces a fixed factor; `1` is the exact pre-zoom render.
   * See `lib/conversation-zoom.ts` for the width→factor curve.
   */
  contentZoom?: number | "auto";
}

export function ConversationShell({
  messages,
  streamingBlocks,
  isStreaming,
  activity,
  stalled,
  onReleaseStalledRun,
  error,
  onRetry,
  header,
  aboveInput,
  composer,
  footer,
  loading,
  fallback,
  testId,
  role,
  ariaLabel,
  dataSessionKey,
  className,
  contentZoom = "auto",
  historyReadError,
  hasMoreHistory,
  loadingOlder,
  olderError,
  onLoadOlder,
}: ConversationShellProps) {
  // Keep the transcript pinned to the bottom across REFLOWS (focus pane
  // open/resize, sidebar toggles, case switches). Native scroll anchoring
  // handles appended content but not a width change, which re-wraps every
  // message and strands the reader short of the end.
  const scrollBodyRef = useRef<HTMLDivElement | null>(null);
  useStickToBottom(scrollBodyRef);

  // ── P6 item 2 — back-scroll pagination mechanics ──────────────────────────
  const { t } = useTranslation();
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  /** Scroll metrics captured at fetch time; consumed by the layout effect
   *  that restores the reader's position after the prepend commits. */
  const preserveRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const requestOlder = useCallback(() => {
    if (!onLoadOlder) return;
    const el = scrollBodyRef.current;
    if (el) preserveRef.current = { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop };
    onLoadOlder();
  }, [onLoadOlder]);
  // Refs so the observer callback reads CURRENT gate state without re-arming
  // on every render.
  const olderGateRef = useRef({ hasMoreHistory, loadingOlder });
  olderGateRef.current = { hasMoreHistory, loadingOlder };
  const requestOlderRef = useRef(requestOlder);
  requestOlderRef.current = requestOlder;
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const rootEl = scrollBodyRef.current;
    if (!sentinel || !rootEl || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        const gate = olderGateRef.current;
        if (!gate.hasMoreHistory || gate.loadingOlder) return;
        requestOlderRef.current();
      },
      { root: rootEl },
    );
    io.observe(sentinel);
    return () => io.disconnect();
    // `loadingOlder` is deliberately a dep: IntersectionObserver fires its
    // callback on observe() with the CURRENT state, so re-arming after each
    // page settles is what keeps loading when the sentinel is still in view
    // (a thread shorter than the viewport) — without it, a static
    // intersection never re-fires and pagination stalls after one page.
  }, [hasMoreHistory, loadingOlder]);

  // Restore the reader's position after a prepend: advance scrollTop by
  // exactly the height the new block added above, so the previously-visible
  // row stays put instead of the viewport jumping to the new page's top.
  useLayoutEffect(() => {
    const saved = preserveRef.current;
    const el = scrollBodyRef.current;
    if (!saved || !el) return;
    if (el.scrollHeight > saved.scrollHeight) {
      el.scrollTop = saved.scrollTop + (el.scrollHeight - saved.scrollHeight);
      preserveRef.current = null;
    }
  }, [messages]);

  // Compact-mode zoom. Measure the ROOT (never zoomed — the zoom lives on an
  // inner wrapper) so the factor is derived from the TRUE available box and can
  // never feed back into its own measurement. Both dimensions are needed for the
  // fill compensation below. Observed unconditionally so an explicit numeric
  // `contentZoom` also gets the compensation dims.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r && r.width > 0 && r.height > 0) setBox({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const zoom = contentZoom === "auto" ? zoomForWidth(box.w) : contentZoom;

  // Fill compensation. A `zoom` of z shrinks the inner box to z× its layout
  // size, so to still fill the root the inner must lay out at 1/z × the root —
  // its content then reads at a roomier width before scaling down, and the
  // fixed-bottom composer still lands at the true bottom.
  //
  // The compensation MUST be in fixed px, not `%`: current Chromium resolves a
  // percentage length in the element's own ZOOMED coordinate space, so
  // `width: 1/z %` exactly cancels the zoom (verified: 200%+zoom:0.5 renders at
  // 200%, while 200px+zoom:0.5 renders at 100px). Deriving px from the measured
  // root sidesteps that entirely. At z = 1 (or before the first measurement) we
  // emit NO inline style, so the non-compact render is byte-identical to before.
  // Compact = actually shrunk. Drives BOTH the zoom fill AND the reading-font
  // swap: Source Serif 4 goes spindly at the small sizes a squeezed column
  // zooms to, so `--font-reading` flips to the UI sans (DM Sans) for the whole
  // conversation subtree. One trigger for both effects so the font never flips
  // independently of the zoom mid-drag.
  // P6 item 2 — the body's empty-vs-readError-vs-fallback decision lives in a
  // pure function so the third-state invariant is pinned by a unit test
  // (`lib/chat/history-body-state.ts`).
  const bodyState = historyBodyState({
    messageCount: messages.length,
    streamingBlockCount: streamingBlocks.length,
    hasError: Boolean(error),
    hasFallback: Boolean(fallback),
    historyReadError,
  });

  const compact = zoom < 1 && box.w > 0;
  const zoomStyle = compact
    ? {
        zoom,
        width: `${Math.round(box.w / zoom)}px`,
        height: `${Math.round(box.h / zoom)}px`,
        // Cascades to every `font-reading` element inside (assistant prose,
        // thinking block, todo card); serif everywhere else.
        "--font-reading": "var(--font-sans)",
      }
    : undefined;

  return (
    <div
      ref={rootRef}
      data-testid={testId}
      data-session-key={dataSessionKey}
      data-content-zoom={zoom}
      data-compact={compact}
      role={role}
      aria-label={ariaLabel}
      className={cn(
        // The conversation reads on the warm cream canvas (plan §6.6):
        // assistant prose lives directly on it, not in an elevated card.
        // `w-full min-w-0` makes the shell fill its parent's full width
        // (the home `main` flex slot / the case-workspace resizable Panel)
        // so the scroll body — and thus the scrollbar — reaches the window's
        // right edge; the inner reading column stays centred via its own
        // `mx-auto max-w-[720px]`. `overflow-hidden` clips the inner zoom
        // wrapper's pre-scale overflow (it lays out at 1/z width before the
        // zoom shrinks it back to fit).
        "flex h-full w-full min-w-0 flex-col overflow-hidden bg-cream-50 dark:bg-coffee-950",
        className,
      )}
    >
      {/* Zoom wrapper — the whole column (transcript AND composer) scales as
          one unit, so a compact conversation is a faithful miniature rather
          than tiny text in full-size chrome. Kept as a distinct child of the
          identity root so the ResizeObserver never measures a zoomed element. */}
      <div className="flex min-h-0 flex-1 flex-col" style={zoomStyle}>
      {header}

      <div ref={scrollBodyRef} className="flex-1 overflow-auto">
        {bodyState === "fallback" ? (
          // Phase 8 — the static fallback replaces the list ONLY for the
          // empty state. A failed/cold opening on a surface that already
          // has history (e.g. returning_after_gap) must keep the
          // transcript visible, not bury it under the fallback.
          fallback
        ) : bodyState === "read-error" ? (
          // P6 item 2 — the THIRD STATE. Empty WITHOUT readError means "no
          // history" (the ordinary empty state renders); empty WITH it means
          // "we could not look", and rendering that as an empty thread shows
          // a lawyer an empty case that is not empty.
          <div
            data-testid="chat-history-read-error"
            className="flex h-full items-center justify-center px-6 text-center text-sm text-coffee-500 dark:text-cream-400"
          >
            {t("chat.history.loadFailed")}
          </div>
        ) : (
          <>
            {(hasMoreHistory || loadingOlder || olderError) && (
              <div
                ref={topSentinelRef}
                data-testid="chat-history-top-sentinel"
                className="flex items-center justify-center gap-2 py-3 text-xs text-coffee-500 dark:text-cream-400"
              >
                {olderError ? (
                  <>
                    <span data-testid="chat-history-older-error">
                      {t("chat.history.olderFailed")}
                    </span>
                    {/* An EXPLICIT user retry — the cursor rejection is never
                        re-issued bare (a bad cursor answered with the tail
                        would splice the newest block into scrollback). */}
                    <button
                      type="button"
                      data-testid="chat-history-older-retry"
                      onClick={requestOlder}
                      className="underline underline-offset-2 hover:text-coffee-700 dark:hover:text-cream-200"
                    >
                      {t("chat.history.retry")}
                    </button>
                  </>
                ) : (
                  <span aria-live="polite">
                    {loadingOlder ? t("chat.history.loadingOlder") : " "}
                  </span>
                )}
              </div>
            )}
            {loading}
            <ChatMessageList
              stalled={stalled}
              onReleaseStalledRun={onReleaseStalledRun}
              messages={messages}
              streamingBlocks={streamingBlocks}
              isStreaming={isStreaming}
              activity={activity}
              error={error}
              onRetry={onRetry}
            />
          </>
        )}
      </div>

      </div>

      {/* ⚖ THE COMPOSER SITS OUTSIDE THE ZOOM — operator ruling 2026-08-10:
          *"the promptbox should not be affected at all by the zoom effect …
          the promptbox original fontsizes should be preserved."*

          Measured before and after, at the viewer-open width the ruling was
          made about (pane 447px, zoom pinned to its floor): the promptbox's
          own 16px was rendering at 10.4px. Its CSS never changed — the type is
          correct in the stylesheet and wrong on screen — which is why this is
          invisible to any check that reads a computed style instead of
          multiplying the zooms between the element and the root.

          This is the SECOND tenant to move out for this reason; the footer
          infobar went first (10px → 6.5px). The transcript still scales,
          because a faithful miniature is right for PROSE the lawyer is
          skimming and wrong for a CONTROL they have to type into.

          `aboveInput` and the ThinkingCue come along deliberately: the cue is
          positioned against the composer's own centred 720px column, so
          leaving it behind would misalign it from the box it points at, and
          `aboveInput` is composer-adjacent chrome by definition.

          Clipping hazard, checked the same way the footer's was: the wrapper
          is height-compensated (`height: box.h / zoom`) inside an
          `overflow-hidden` root, so a sibling after it could be clipped to
          nothing. `flex-1 min-h-0` lets the flex algorithm shrink the wrapper
          to make room — verified in the browser at both regimes below. */}
      {aboveInput}

      {/* ⚖ THE FIXED "working" CUE WAS RETIRED HERE (L0, 2026-08-13). It now
          renders as the forming turn's header at the TRANSCRIPT TAIL, inside
          `ChatMessageList` — where it scrolls with the work accumulating
          beneath it, and where the shell's `useStickToBottom` ResizeObserver
          actually watches it. A fixed duplicate is deliberately NOT kept:
          off-screen visibility is the existing scroll-to-bottom affordance's
          job (plan §4 L0(a)).

          Do not re-add a cue in this slot. Anything mounted here is OUTSIDE the
          scroll body, so its growth is invisible to that observer — which is
          the precise reason the relocation had to happen rather than a second
          copy being rendered. `ThinkingCue` itself still exists and is still
          used by `LexiunStatusCard` on /home. */}

      {composer}

      {/* The footer sits OUTSIDE the zoom wrapper — MEASURED, not assumed
          (miniplan D8, resolved at N2 with a real footer child in the
          browser). `zoomStyle` scales the conversation to a 0.65 floor as the
          pane narrows, which is right for the transcript and wrong for status
          chrome: inside the wrapper, at the viewer-open width (pane ~397px),
          the infobar's 10px type rendered at 6.5px in a 14.3px strip —
          unreadable, and silently so, because nothing errors.

          The hazard checked before moving it: this wrapper is height-
          compensated to fill the root (`height: box.h / zoom`) and the root is
          `overflow-hidden`, so a sibling after it could have been clipped to
          nothing — an invisible bar is worse than a small one. Measured at
          both regimes: the bar keeps its full height and sits flush with the
          pane's bottom edge, because `flex-1 min-h-0` (flex-basis: 0%) lets
          the flex algorithm shrink the wrapper to make room.

          Width-coupling is unaffected: the identity root is still
          `w-full min-w-0`, so the footer shrinks and grows with the pane and
          never spans another column. */}
      {footer}
    </div>
  );
}
